<script setup lang="ts">
import {
  BRAND_APP_ORDER_CHANNELS,
  type BrandAppOrderChannelId,
  type BrandAppShowcaseStore
} from '../../../lib/brandAppContract'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import { isInvalidUrlInput } from '../../../lib/brandAppUrl'

const props = defineProps<{
  channel: BrandAppOrderChannelId
  stores: BrandAppShowcaseStore[]
}>()

const { t } = useI18n()
const { order, updateOrderChannel, setOrderChannelStoreUrl, isStoreHidden } = useBrandAppConfig()

const urlErrors = (value: string | undefined) =>
  isInvalidUrlInput(value) ? [t('pages.loyaltySupports.webapp.editor.validation.invalidUrl')] : []

const CHANNELS_BY_ID = Object.fromEntries(BRAND_APP_ORDER_CHANNELS.map((item) => [item.id, item])) as Record<
  BrandAppOrderChannelId,
  (typeof BRAND_APP_ORDER_CHANNELS)[number]
>

const definition = computed(() => CHANNELS_BY_ID[props.channel])

const config = computed(() => order.value.channels[props.channel])

const visibleStores = computed(() => props.stores.filter((store) => !isStoreHidden(store.id)))

const hasSeveralStores = computed(() => visibleStores.value.length > 1)

const isPerStore = computed(() => hasSeveralStores.value && config.value.perStore)

const onModeChange = (mode: string | number) => {
  updateOrderChannel(props.channel, 'perStore', mode === 'perStore')
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <Tabs
      v-if="hasSeveralStores"
      :model-value="config.perStore ? 'perStore' : 'shared'"
      @update:model-value="onModeChange"
    >
      <TabsList class="w-full">
        <TabsTrigger value="shared" class="flex-1">{{
          t('pages.loyaltySupports.webapp.editor.order.fields.shared')
        }}</TabsTrigger>
        <TabsTrigger value="perStore" class="flex-1">{{
          t('pages.loyaltySupports.webapp.editor.order.fields.perStore')
        }}</TabsTrigger>
      </TabsList>
    </Tabs>

    <template v-if="isPerStore">
      <p class="text-muted-foreground text-xs">
        {{ t('pages.loyaltySupports.webapp.editor.order.fields.perStoreHint', { channel: definition.label }) }}
      </p>
      <AppFormInput
        v-for="store in visibleStores"
        :id="`brandApp.order.${channel}.store.${store.id}`"
        :key="store.id"
        :label="store.name"
        :model-value="config.storeUrls[store.id] ?? ''"
        :errors="urlErrors(config.storeUrls[store.id])"
        @update:model-value="setOrderChannelStoreUrl(channel, store.id, String($event))"
      />
    </template>

    <AppFormInput
      v-else
      :id="`brandApp.order.${channel}.url`"
      :label="t('pages.loyaltySupports.webapp.editor.order.fields.link')"
      :model-value="config.url"
      :errors="urlErrors(config.url)"
      @update:model-value="updateOrderChannel(channel, 'url', String($event))"
    />
  </div>
</template>
